/**
 * @file       Phone format directive for local/international numbers
 * @version    1.0.0
 * @author     Boris Veits
 * @copyright  Copyright (c) 2018, MindShare HDV LLC.  All rights reserved.
 *             Property of MindShare HDV LLC.  This file cannot be used or
 *             altered in anyway without expressed written permission from
 *             MindShare HDV LLC.
 */
import { Directive, ElementRef, HostListener, Optional } from '@angular/core';
import { NgControl } from '@angular/forms';
import { formatPhoneNumber } from '@app/secure/helper/phoneFormat.helper';

@Directive
({
    selector: '[appPhoneFormat]',
    standalone: false
})

/**
 * @desc   - directive for formatting local and international phone numbers
 */
class PhoneFormatDirective
{
    private updating: boolean;

    /**
     * @desc   - directive constructor
     * @access - public
     * @param  - el : ElementRef
     * @param  - ngControl : NgControl (optional)
     * @return -
     */
    constructor
    (
        private el: ElementRef,
        @Optional() private ngControl: NgControl
    )
    {
        this.updating = false;
    }

    //__________________ P R I V A T E  M E T H O D S _____________________
    private applyFormatting
    (
        inputElement: HTMLInputElement
    ): void
    {
        let currentValue: string;
        let formattedValue: string;

        if (this.updating)
        {
            return;
        }

        currentValue = inputElement.value || '';
        formattedValue = formatPhoneNumber(currentValue);

        if (formattedValue !== currentValue)
        {
            this.updating = true;
            inputElement.value = formattedValue;
            if (this.ngControl?.control)
            {
                this.ngControl.control.setValue(
                    formattedValue,
                    { emitEvent: false }
                );
            }
            this.updating = false;
        }
    }

    //___________________ P U B L I C  M E T H O D S ______________________
    @HostListener('blur', ['$event'])
    onBlur
    (
        event: FocusEvent
    ): void
    {
        let inputElement: HTMLInputElement;

        inputElement = event.target as HTMLInputElement;
        this.applyFormatting(inputElement);
    }

    @HostListener('input', ['$event'])
    onInput
    (
        event: InputEvent
    ): void
    {
        let inputElement: HTMLInputElement;

        inputElement = event.target as HTMLInputElement;
        this.applyFormatting(inputElement);
    }

    @HostListener('paste', ['$event'])
    onPaste
    (
        event: ClipboardEvent
    ): void
    {
        let inputElement: HTMLInputElement;

        setTimeout(() =>
        {
            inputElement = event.target as HTMLInputElement;
            this.applyFormatting(inputElement);
        }, 0);
    }
}

export { PhoneFormatDirective };
